import { useTranslation } from 'react-i18next'
import React, { useState } from 'react'

import { useAssets } from './_load/AssetsLoader'
import { useSaveAssets } from './_requests/saveAssets'
import BulkForm from '../../../packages/kiti/components/form/form/BulkForm'
import Form from '../../../packages/kiti/components/form/form/Form'
import { useFormState } from '../../../packages/kiti/components/form/form/FormHandler'
import Line from '../../../packages/kiti/components/helpers/Line'
import PlaceSelect from '../../../containers/places/select/PlaceSelect'
import Scroll from '../../../packages/kiti/components/helpers/Scroll'
import Select from '../../../packages/kiti/components/form/Select'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import SubmitBar from '../../../packages/kiti/components/form/SubmitBar'
import MinicrudSelect from '../../../containers/minicruds/select/MinicrudSelect'
import { formatFieldValues, useFieldsForBulkForm } from '../../../utils/helpers/fields'
import Loading from '../../../packages/kiti/components/helpers/Loading'
import { usePlace } from '../../../modules/places/show/_load/PlaceLoader'
import { usePlaceTypesWithPermission } from '../../../utils/helpers/placeTypes'
import Textarea from '../../../components/form/Textarea'

const AssetsBulkForm = ({ disabled, hasAssets, enableMultiEdit, fields, initialItemValue }) => {
  const { t } = useTranslation('editAsset')
  const { handleChangeIn } = useFormState()
  const placeTypes = usePlaceTypesWithPermission('list')
  const [editableAssetIndex] = useState(null)

  const shouldDisable = (value, index) => {
    if (!disabled || !hasAssets || !value.id) return false

    if (enableMultiEdit) {
      return !value.__multiEdit
    }

    if (editableAssetIndex !== null && index !== editableAssetIndex) return true

    if (editableAssetIndex === null) return true

    return false
  }

  const handleAfterChangePlaceType = (_value, { path }) => {
    handleChangeIn([...path, 'place'], undefined)
  }

  const baseFields = [
    {
      label: 'Nome*',
      name: 'name',
      width: 15,
      disabled: (value, index) => shouldDisable(value, index),
      keepValueWhenDisabled: true
    },
    ...(disabled
      ? []
      : [
          {
            label: t('placeType'),
            name: 'placeType',
            Input: Select,
            afterChange: handleAfterChangePlaceType,
            options: placeTypes.map(type => ({ id: type.id, value: type.key, name: type.namePlural }))
          },
          {
            label: t('place'),
            name: 'place',
            Input: PlaceSelect,
            dynamicProps: item => ({ type: item?.placeType?.value }),
            keepValueWhenDisabled: true
          }
        ]),
    {
      label: 'Tipo',
      name: 'type',
      Input: MinicrudSelect,
      entity: 'assetType',
      disabled: (value, index) => shouldDisable(value, index),
      keepValueWhenDisabled: true
    },
    ...fields.map(field => {
      if (field.type === 'text') {
        return {
          ...field,
          Input: Textarea,
          disabled: (value, index) => shouldDisable(value, index),
          keepValueWhenDisabled: true
        }
      }

      return {
        ...field,
        disabled: (value, index) => shouldDisable(value, index),
        keepValueWhenDisabled: true
      }
    })
  ]

  return (
    <Scroll height={disabled ? '60vh' : '69vh'} scroll scrollX direction="row">
      <Separator weight={8} flex={1}>
        <BulkForm
          isEditing={disabled ? false : hasAssets}
          initialItemState={initialItemValue}
          fields={baseFields}
          disableMultiEdit={disabled && !enableMultiEdit}
          onEditItem={null}
          hideDuplicateButton={disabled}
        />
      </Separator>
    </Scroll>
  )
}

const EditAssetsForm = ({ disabled, externalAssets, enableMultiEdit = false }) => {
  const [fieldsData, { loading: loadingFields }] = useFieldsForBulkForm('assets')
  const [onSubmit, { loading, error }] = useSaveAssets(disabled)
  const loadedAssets = useAssets()
  const place = usePlace()

  const assets = disabled && externalAssets ? externalAssets : loadedAssets

  if (loadingFields) return <Loading />

  const fields = fieldsData.filter(field => field.type !== 'gallery')

  const hasAssets = !!assets?.length

  const initialItemValue = {
    fieldValues: formatFieldValues([], 'assets'),
    ...(disabled && place ? { place } : {})
  }

  const initialState = hasAssets
    ? assets.map(item => {
        const processedItem = disabled
          ? { ...item, place, fieldValues: formatFieldValues(item.fieldValues, 'assets') }
          : { ...item, fieldValues: formatFieldValues(item.fieldValues, 'assets') }
        return processedItem
      })
    : [initialItemValue]

  return (
    <Form loading={loading} onSubmit={onSubmit} initialState={initialState}>
      <AssetsBulkForm
        disabled={disabled}
        hasAssets={hasAssets}
        enableMultiEdit={enableMultiEdit}
        fields={fields}
        initialItemValue={initialItemValue}
      />
      <Line />
      <Separator paddingH={2}>
        <SubmitBar loading={loading} error={error} />
      </Separator>
    </Form>
  )
}

export default EditAssetsForm
